import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { array, concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import style from 'ember-style-modifier/modifiers/style';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { formatCurrency } from 'tio-reimagined/format';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import { eq, not, and } from 'tio-ui/utilities';
import {
  PhRocket,
  PhCheckCircle,
  PhArrowRight,
  PhArrowLeft,
  PhBank,
  PhGraduationCap,
  PhBuildings,
  PhMagnifyingGlass,
  PhHandshake,
  PhSparkle,
  PhShieldCheck,
  PhTarget,
  PhChartLineUp,
  PhCurrencyDollar,
  PhBriefcase,
  PhWarning,
  PhInfo,
  PhSealCheck,
  PhConfetti,
  PhHeart,
  PhStar,
  PhLightning,
  PhPlugsConnected,
  PhCloudArrowUp,
  PhPencilSimple,
  PhQuestion,
  PhCaretRight,
  PhPlus,
  PhX,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoClientId,
} from 'tio-employee/utils/career-pathways-demo-data';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type RouterService from '@ember/routing/router-service';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';

interface OnboardingSignature {
  Args: {
    model: unknown;
  };
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  icon: typeof PhRocket;
  status: 'completed' | 'current' | 'upcoming';
  optional?: boolean;
}

interface BenefitPreview {
  id: string;
  title: string;
  description: string;
  value: string;
  icon: typeof PhCurrencyDollar;
  color: string;
}

export default class OnboardingIndex extends Component<OnboardingSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare router: RouterService;
  @service declare reimaginedData: ReimaginedDataService;

  @tracked currentStep = 1;
  @tracked hasLinkedLoans = false;
  @tracked hasCompletedPslfCheck = false;
  @tracked hasCompletedLrapDiscovery = false;
  @tracked hasSetGoal = false;

  // Loan connection flow state
  @tracked loanConnectionMethod: 'instant' | 'manual' | 'upload' | null = null;
  @tracked isConnecting = false;
  @tracked connectionProgress = 0;
  @tracked discoveredLoans: Array<{
    id: string;
    servicer: string;
    type: 'federal' | 'private';
    loanType: string;
    balance: number;
    interestRate: number;
    monthlyPayment: number;
    status: string;
  }> = [];
  @tracked showManualEntry = false;
  @tracked manualLoans: Array<{
    id: string;
    servicer: string;
    balance: string;
    interestRate: string;
    monthlyPayment: string;
    loanType: string;
  }> = [];
  @tracked showUploadOption = false;
  @tracked uploadedFile: string | null = null;
  @tracked hasPrivateLoans = false;

  // Demo: selected loan servicer
  @tracked selectedServicer: string = '';
  // Demo: PSLF check answers
  @tracked worksInPublicService: boolean | null = null;
  @tracked yearsInService = 0;
  // Demo: selected goal
  @tracked selectedGoal: string = '';

  get totalSteps() {
    return this._stepDefinitions.length;
  }

  get _stepDefinitions() {
    const steps: OnboardingStep[] = [
      {
        id: 'welcome',
        title: 'Welcome',
        description: 'Discover your benefits',
        icon: PhSparkle,
        status: 'upcoming' as const,
      },
    ];

    if (this.scenario.features.studentLoanRepayment) {
      steps.push({
        id: 'loans',
        title: 'Link Loans',
        description: 'Upload your loan documents',
        icon: PhBank,
        status: 'upcoming' as const,
      });
    }

    if (this.scenario.features.pslf) {
      steps.push({
        id: 'pslf',
        title: 'PSLF Check',
        description: 'Check forgiveness eligibility',
        icon: PhShieldCheck,
        status: 'upcoming' as const,
      });
    }

    if (this.showLrapStep) {
      steps.push({
        id: 'lrap',
        title: 'LRAP Discovery',
        description: 'Find assistance programs',
        icon: PhMagnifyingGlass,
        status: 'upcoming' as const,
        optional: true,
      });
    }

    steps.push({
      id: 'goals',
      title: 'Set Your Goal',
      description: 'Choose your path',
      icon: PhTarget,
      status: 'upcoming' as const,
    });

    return steps;
  }

  get steps(): OnboardingStep[] {
    return this._stepDefinitions.map((step, index) => {
      const stepNum = index + 1;
      return {
        ...step,
        status:
          this.currentStep > stepNum
            ? ('completed' as const)
            : this.currentStep === stepNum
              ? ('current' as const)
              : ('upcoming' as const),
      };
    });
  }
  // Inverse getters for template use (avoids unless/else lint error)
  get needsLoanLink() {
    return !this.hasLinkedLoans;
  }

  get needsPslfCheck() {
    return !this.hasCompletedPslfCheck;
  }

  get needsLrapDiscovery() {
    return !this.hasCompletedLrapDiscovery;
  }

  get needsGoal() {
    return !this.hasSetGoal;
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get clientId() {
    return getCurrentDemoClientId();
  }

  get progressPercent() {
    return Math.round((this.currentStep / this.totalSteps) * 100);
  }

  get benefitPreviews(): BenefitPreview[] {
    const benefits: BenefitPreview[] = [];

    if (this.scenario.features.studentLoanRepayment) {
      benefits.push({
        id: 'slr',
        title: 'Student Loan Repayment',
        description: 'Tax-free employer contributions',
        value: formatCurrency(5250) + '/year',
        icon: PhHandshake,
        color: 'emerald',
      });
    }

    if (this.scenario.features.tuitionAssistance) {
      benefits.push({
        id: 'ta',
        title: 'Tuition Assistance',
        description: 'Advance your education',
        value: formatCurrency(5250) + '/year',
        icon: PhGraduationCap,
        color: 'violet',
      });
    }

    if (this.scenario.features.pslf) {
      benefits.push({
        id: 'pslf',
        title: 'PSLF Tracking',
        description: 'Track your forgiveness progress',
        value: 'Up to 100% forgiven',
        icon: PhShieldCheck,
        color: 'blue',
      });
    }

    benefits.push({
      id: 'tools',
      title: 'Financial Tools',
      description: 'Calculators & strategy finder',
      value: 'Always free',
      icon: PhChartLineUp,
      color: 'amber',
    });

    return benefits;
  }

  get showLrapStep() {
    // Show LRAP step for legal and healthcare clients
    return this.clientId === 'legal' || this.clientId === 'advocate';
  }

  // Loan servicers for demo
  get servicers() {
    return [
      { id: 'mohela', name: 'MOHELA', popular: true, logo: '🏛️' },
      { id: 'nelnet', name: 'Nelnet', popular: true, logo: '🏦' },
      { id: 'aidvantage', name: 'Aidvantage', popular: true, logo: '💰' },
      { id: 'edfinancial', name: 'EdFinancial', popular: false, logo: '📊' },
      { id: 'osla', name: 'OSLA', popular: false, logo: '🎓' },
      { id: 'ecsi', name: 'ECSI', popular: false, logo: '📋' },
    ];
  }

  get popularServicers() {
    return this.servicers.filter((s) => s.popular);
  }

  get totalDiscoveredBalance() {
    return this.discoveredLoans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalMonthlyPayment() {
    return this.discoveredLoans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get avgInterestRate() {
    if (!this.discoveredLoans.length) return 0;
    const weighted = this.discoveredLoans.reduce(
      (sum, loan) => sum + loan.balance * loan.interestRate,
      0
    );
    return weighted / this.totalDiscoveredBalance;
  }

  get federalLoans() {
    return this.discoveredLoans.filter((loan) => loan.type === 'federal');
  }

  get privateLoans() {
    return this.discoveredLoans.filter((loan) => loan.type === 'private');
  }

  get unlockableValue() {
    // Calculate potential value user can unlock
    const section127 = 5250;
    const pslfPotential = this.clientId === 'advocate' ? 45000 : 0;
    return { section127, pslfPotential, total: section127 + (pslfPotential > 0 ? 50000 : 0) };
  }

  get hasPslfPotential() {
    return this.unlockableValue.pslfPotential >= 1;
  }

  get canRemoveManualLoan() {
    return this.manualLoans.length >= 2;
  }

  get hasManualLoans() {
    return this.manualLoans.length > 0;
  }

  get pslfEligibilityResult() {
    if (this.worksInPublicService === null) return null;
    if (!this.worksInPublicService) {
      return {
        eligible: false,
        message:
          'Your current employer may not qualify for PSLF. But you can still benefit from IDR plans and other forgiveness options.',
        suggestion: 'Use our Strategy Finder to explore your best repayment options.',
      };
    }
    return {
      eligible: true,
      estimatedPayments: Math.max(0, 120 - this.yearsInService * 12),
      estimatedForgiveness: 45000 + Math.random() * 30000,
      message: `Great news! You appear eligible for PSLF with approximately ${Math.max(0, 120 - this.yearsInService * 12)} payments remaining.`,
    };
  }

  get lrapDiscoveryResult() {
    if (!this.showLrapStep) return null;

    if (this.clientId === 'legal') {
      return {
        programsFound: 3,
        potentialValue: 17000,
        programs: [
          { name: 'Georgetown Law LRAP', value: 12000, type: 'school' },
          { name: 'DC Bar Foundation LRAP', value: 5000, type: 'state' },
          { name: 'Employer Section 127', value: 5250, type: 'employer' },
        ],
      };
    }

    if (this.clientId === 'advocate') {
      return {
        programsFound: 2,
        potentialValue: 42500,
        programs: [
          { name: 'NHSC Loan Repayment', value: 37500, type: 'federal' },
          { name: 'Illinois Nurse Educator LRAP', value: 5000, type: 'state' },
        ],
      };
    }

    return null;
  }

  get goalOptions() {
    const goals: Array<{
      id: string;
      title: string;
      description: string;
      icon: typeof PhStar;
      color: string;
    }> = [];

    if (this.scenario.features.studentLoanRepayment) {
      goals.push(
        {
          id: 'pay-off-fast',
          title: 'Pay Off Loans Fast',
          description: 'I want to eliminate my debt as quickly as possible',
          icon: PhLightning,
          color: 'amber',
        },
        {
          id: 'minimize-payments',
          title: 'Minimize Monthly Payments',
          description: 'I need lower payments to manage my budget',
          icon: PhCurrencyDollar,
          color: 'blue',
        }
      );

      if (this.scenario.features.pslf) {
        goals.push({
          id: 'maximize-forgiveness',
          title: 'Maximize Forgiveness',
          description: 'I want to minimize what I pay out-of-pocket through PSLF or IDR',
          icon: PhHeart,
          color: 'violet',
        });
      }
    }

    goals.push({
      id: 'advance-career',
      title: 'Advance My Career',
      description: 'I want to use education benefits to grow professionally',
      icon: PhStar,
      color: 'emerald',
    });

    return goals;
  }

  get currentStepId() {
    const step = this._stepDefinitions[this.currentStep - 1];
    return step?.id ?? 'welcome';
  }

  // Navigation
  nextStep = () => {
    if (this.currentStep < this.totalSteps) {
      this.currentStep++;
    }
  };

  previousStep = () => {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  };

  goToStep = (step: number) => {
    if (step <= this.currentStep + 1) {
      this.currentStep = step;
    }
  };

  // Step 2: Link loans
  selectServicer = (servicerId: string) => {
    this.selectedServicer = servicerId;
  };

  // Instant connection via FSV/Plaid-style API
  startInstantConnection = () => {
    // In live mode, navigate to the existing observability upload flow
    // which handles Plaid/Payitoff integration
    if (!this.reimaginedData.isDemoMode) {
      this.router.transitionTo('authenticated.observability.upload', {
        queryParams: {
          source: 'reimagined-onboarding',
          providers: 'statement,nslds',
          returnTo: 'authenticated.reimagined.onboarding',
        },
      });
      return;
    }

    // Demo mode: simulate the connection flow
    this.loanConnectionMethod = 'instant';
    this.isConnecting = true;
    this.connectionProgress = 0;

    // Simulate connection progress
    const progressInterval = setInterval(() => {
      this.connectionProgress += 15;
      if (this.connectionProgress >= 100) {
        clearInterval(progressInterval);
        this.completeInstantConnection();
      }
    }, 400);
  };

  completeInstantConnection = () => {
    // Simulate discovered loans
    this.discoveredLoans = [
      {
        id: '1',
        servicer: 'MOHELA',
        type: 'federal',
        loanType: 'Direct Unsubsidized',
        balance: 28420,
        interestRate: 5.5,
        monthlyPayment: 285,
        status: 'In Repayment',
      },
      {
        id: '2',
        servicer: 'MOHELA',
        type: 'federal',
        loanType: 'Direct Subsidized',
        balance: 18500,
        interestRate: 4.53,
        monthlyPayment: 165,
        status: 'In Repayment',
      },
      {
        id: '3',
        servicer: 'Nelnet',
        type: 'federal',
        loanType: 'Direct PLUS',
        balance: 21500,
        interestRate: 7.08,
        monthlyPayment: 248,
        status: 'In Repayment',
      },
    ];
    this.isConnecting = false;
    this.hasLinkedLoans = true;
  };

  // Manual entry flow
  startManualEntry = () => {
    this.loanConnectionMethod = 'manual';
    this.showManualEntry = true;
    // Add one empty loan to start
    this.addManualLoan();
  };

  addManualLoan = () => {
    this.manualLoans = [
      ...this.manualLoans,
      {
        id: `manual-${Date.now()}`,
        servicer: '',
        balance: '',
        interestRate: '',
        monthlyPayment: '',
        loanType: 'federal',
      },
    ];
  };

  removeManualLoan = (loanId: string) => {
    this.manualLoans = this.manualLoans.filter((loan) => loan.id !== loanId);
  };

  updateManualLoan = (loanId: string, field: string, value: string) => {
    this.manualLoans = this.manualLoans.map((loan) =>
      loan.id === loanId ? { ...loan, [field]: value } : loan
    );
  };

  submitManualLoans = () => {
    // Convert manual entries to discovered loans format
    this.discoveredLoans = this.manualLoans
      .filter((loan) => loan.balance)
      .map((loan) => ({
        id: loan.id,
        servicer: loan.servicer || 'Unknown',
        type: (loan.loanType as 'federal' | 'private') || 'federal',
        loanType: 'Manual Entry',
        balance: parseFloat(loan.balance) || 0,
        interestRate: parseFloat(loan.interestRate) || 5.0,
        monthlyPayment: parseFloat(loan.monthlyPayment) || 0,
        status: 'Entered Manually',
      }));
    this.hasLinkedLoans = true;
    this.showManualEntry = false;
  };

  // Upload flow
  startUpload = () => {
    // In live mode, navigate to the observability upload flow for document upload
    if (!this.reimaginedData.isDemoMode) {
      this.router.transitionTo('authenticated.observability.upload', {
        queryParams: {
          source: 'reimagined-onboarding',
          providers: 'statement', // Document upload only, not instant connect
          returnTo: 'authenticated.reimagined.onboarding',
        },
      });
      return;
    }

    // Demo mode: show upload UI
    this.loanConnectionMethod = 'upload';
    this.showUploadOption = true;
  };

  handleFileUpload = () => {
    // In live mode, the observability upload route handles this
    // This is only for demo mode simulation
    this.uploadedFile = 'loan_statement.pdf';
    // After upload, we'd process and discover loans
    setTimeout(() => {
      this.discoveredLoans = [
        {
          id: '1',
          servicer: 'From Document',
          type: 'federal',
          loanType: 'Direct Unsubsidized',
          balance: 35000,
          interestRate: 5.5,
          monthlyPayment: 350,
          status: 'Pending Verification',
        },
      ];
      this.hasLinkedLoans = true;
    }, 1500);
  };

  // Reset and try different method
  resetConnectionMethod = () => {
    this.loanConnectionMethod = null;
    this.showManualEntry = false;
    this.showUploadOption = false;
    this.isConnecting = false;
    this.connectionProgress = 0;
  };

  confirmLoanLink = () => {
    this.hasLinkedLoans = true;
    this.nextStep();
  };

  skipLoanLink = () => {
    this.nextStep();
  };

  // Step 3: PSLF check
  setPublicServiceAnswer = (answer: boolean) => {
    this.worksInPublicService = answer;
  };

  setYearsInService = (years: number) => {
    this.yearsInService = years;
  };

  completePslfCheck = () => {
    this.hasCompletedPslfCheck = true;
    // Skip LRAP step if not applicable - jump to goals step
    if (!this.showLrapStep) {
      const goalsIndex = this._stepDefinitions.findIndex((s) => s.id === 'goals');
      if (goalsIndex >= 0) {
        this.currentStep = goalsIndex + 1;
      } else {
        this.nextStep();
      }
    } else {
      this.nextStep();
    }
  };

  // Step 4: LRAP discovery
  completeLrapDiscovery = () => {
    this.hasCompletedLrapDiscovery = true;
    this.nextStep();
  };

  skipLrapDiscovery = () => {
    this.nextStep();
  };

  // Step 5: Set goal
  selectGoal = (goal: string) => {
    this.selectedGoal = goal;
  };

  completeOnboarding = () => {
    this.hasSetGoal = true;
    // In production: save preferences and navigate to dashboard
  };

  <template>
    {{! template-lint-disable no-bare-strings style-concatenation require-presentational-children }}
    <DataIndicator @source="demo" @showBadge={{true}} @dataSourceId="employee-profile">
      <VStack class="data-audit w-full min-w-0 max-w-4xl mx-auto">
        {{! Progress Header }}
        <div class="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div class="flex items-center justify-between mb-4">
            <div class="flex items-center gap-3">
              <div class="p-2 bg-indigo-100 rounded-lg">
                <PhRocket @size={{24}} class="text-indigo-600" />
              </div>
              <div>
                <Heading @level={{2}} class="text-lg font-semibold text-gray-900">
                  Getting Started
                </Heading>
                <Text class="text-sm text-gray-500">
                  Step
                  {{this.currentStep}}
                  of
                  {{this.totalSteps}}
                </Text>
              </div>
            </div>
            <div class="text-right">
              <Text class="text-2xl font-bold text-indigo-600">{{this.progressPercent}}%</Text>
              <Text class="text-sm text-gray-500">Complete</Text>
            </div>
          </div>

          {{! Step indicators }}
          <div class="flex items-center gap-2">
            {{#each this.steps as |step index|}}
              <button
                type="button"
                class="flex-1 group"
                disabled={{eq step.status "upcoming"}}
                {{on
                  "click"
                  (fn
                    this.goToStep
                    (if
                      (eq index 0) 1 (if (eq index 1) 2 (if (eq index 2) 3 (if (eq index 3) 4 5)))
                    )
                  )
                }}
              >
                <div
                  class="h-2 rounded-full transition-all
                    {{if
                      (eq step.status 'completed')
                      'bg-emerald-500'
                      (if (eq step.status 'current') 'bg-indigo-500' 'bg-gray-200')
                    }}"
                ></div>
                <div class="mt-2 text-left">
                  <Text
                    class="text-xs font-medium
                      {{if
                        (eq step.status 'current')
                        'text-indigo-600'
                        (if (eq step.status 'completed') 'text-emerald-600' 'text-gray-400')
                      }}"
                  >
                    {{step.title}}
                  </Text>
                </div>
              </button>
            {{/each}}
          </div>
        </div>

        {{! Step Content }}
        <div class="bg-white rounded-xl border border-gray-200 p-8">
          {{! ================================ STEP 1: WELCOME ================================ }}
          {{#if (eq this.currentStepId "welcome")}}
            <div class="text-center mb-8">
              <div
                class="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-2xl flex items-center justify-center"
              >
                <PhSparkle @size={{40}} class="text-white" @weight="fill" />
              </div>
              <Heading @level={{1}} class="text-3xl font-bold text-gray-900 mb-3">
                Welcome to
                {{this.demoClient.client.name}}
                Benefits!
              </Heading>
              <Text class="text-lg text-gray-600 max-w-xl mx-auto">
                You have access to powerful education and financial wellness benefits. Let's
                personalize your experience in just a few minutes.
              </Text>
            </div>

            {{! Benefit Cards }}
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {{#each this.benefitPreviews as |benefit|}}
                <div
                  class="p-5 rounded-xl border-2 transition-all
                    {{if
                      (eq benefit.color 'emerald')
                      'border-emerald-200 bg-emerald-50'
                      (if
                        (eq benefit.color 'violet')
                        'border-violet-200 bg-violet-50'
                        (if
                          (eq benefit.color 'blue')
                          'border-blue-200 bg-blue-50'
                          'border-amber-200 bg-amber-50'
                        )
                      )
                    }}"
                >
                  <div class="flex items-start gap-4">
                    <div
                      class="p-3 rounded-lg
                        {{if
                          (eq benefit.color 'emerald')
                          'bg-emerald-100 text-emerald-600'
                          (if
                            (eq benefit.color 'violet')
                            'bg-violet-100 text-violet-600'
                            (if
                              (eq benefit.color 'blue')
                              'bg-blue-100 text-blue-600'
                              'bg-amber-100 text-amber-600'
                            )
                          )
                        }}"
                    >
                      <benefit.icon @size={{24}} />
                    </div>
                    <div class="flex-1">
                      <Text class="font-semibold text-gray-900">{{benefit.title}}</Text>
                      <Text class="text-sm text-gray-600">{{benefit.description}}</Text>
                      <Text
                        class="text-lg font-bold mt-1
                          {{if
                            (eq benefit.color 'emerald')
                            'text-emerald-700'
                            (if
                              (eq benefit.color 'violet')
                              'text-violet-700'
                              (if (eq benefit.color 'blue') 'text-blue-700' 'text-amber-700')
                            )
                          }}"
                      >
                        {{benefit.value}}
                      </Text>
                    </div>
                  </div>
                </div>
              {{/each}}
            </div>

            <div class="flex justify-center">
              <Button
                @intent="primary"
                @size="lg"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Let's Get Started"
                {{on "click" this.nextStep}}
              />
            </div>
          {{/if}}

          {{! ================================ STEP 2: LINK LOANS (REIMAGINED) ================================ }}
          {{#if (eq this.currentStepId "loans")}}
            {{#if this.hasLinkedLoans}}
              {{! SUCCESS STATE: Loans discovered }}
              <div class="mb-6">
                <div class="flex items-center gap-3 mb-2">
                  <div class="p-2 bg-emerald-100 rounded-lg">
                    <PhCheckCircle @size={{28}} class="text-emerald-600" />
                  </div>
                  <div>
                    <Heading @level={{2}} class="text-2xl font-bold text-gray-900">
                      Loans Connected Successfully!
                    </Heading>
                    <Text class="text-gray-600">
                      We found
                      {{this.discoveredLoans.length}}
                      loans. Here's what we discovered:
                    </Text>
                  </div>
                </div>
              </div>

              {{! Summary Cards }}
              <div class="grid grid-cols-3 gap-4 mb-6">
                <div
                  class="bg-gradient-to-br from-indigo-50 to-violet-50 rounded-xl p-4 border border-indigo-100"
                >
                  <Text class="text-sm text-indigo-600 font-medium">Total Balance</Text>
                  <Text class="text-2xl font-bold text-indigo-900">
                    {{formatCurrency this.totalDiscoveredBalance}}
                  </Text>
                </div>
                <div
                  class="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100"
                >
                  <Text class="text-sm text-emerald-600 font-medium">Monthly Payment</Text>
                  <Text class="text-2xl font-bold text-emerald-900">
                    {{formatCurrency this.totalMonthlyPayment}}/mo
                  </Text>
                </div>
                <div
                  class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100"
                >
                  <Text class="text-sm text-amber-600 font-medium">Avg. Interest</Text>
                  <Text class="text-2xl font-bold text-amber-900">
                    {{this.avgInterestRate}}%
                  </Text>
                </div>
              </div>

              {{! Discovered Loans List }}
              <div class="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
                <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <Text class="font-medium text-gray-700">Your Student Loans</Text>
                </div>
                <div class="divide-y divide-gray-100">
                  {{#each this.discoveredLoans as |loan|}}
                    <div class="p-4 flex items-center gap-4">
                      <div
                        class="p-2 rounded-lg
                          {{if (eq loan.type 'federal') 'bg-blue-100' 'bg-purple-100'}}"
                      >
                        <PhBank
                          @size={{20}}
                          class={{if (eq loan.type "federal") "text-blue-600" "text-purple-600"}}
                        />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <Text class="font-medium text-gray-900">{{loan.loanType}}</Text>
                          <Chip
                            @intent={{if (eq loan.type "federal") "primary" "default"}}
                            @size="sm"
                          >
                            {{loan.type}}
                          </Chip>
                        </div>
                        <Text class="text-sm text-gray-500">{{loan.servicer}}
                          ·
                          {{loan.status}}</Text>
                      </div>
                      <div class="text-right">
                        <Text class="font-semibold text-gray-900">{{formatCurrency
                            loan.balance
                          }}</Text>
                        <Text class="text-sm text-gray-500">{{loan.interestRate}}% ·
                          {{formatCurrency loan.monthlyPayment}}/mo</Text>
                      </div>
                    </div>
                  {{/each}}
                </div>
              </div>

              {{! What You've Unlocked }}
              <div
                class="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border border-emerald-200 mb-6"
              >
                <div class="flex items-start gap-4">
                  <div class="p-2 bg-emerald-100 rounded-lg">
                    <PhSparkle @size={{24}} class="text-emerald-600" />
                  </div>
                  <div>
                    <Text class="font-semibold text-emerald-900">You've Unlocked These Benefits!</Text>
                    <div class="mt-3 grid grid-cols-2 gap-3">
                      <div class="flex items-center gap-2">
                        <PhCheckCircle @size={{18}} class="text-emerald-600" />
                        <Text class="text-sm text-emerald-800">Employer payments toward your loans</Text>
                      </div>
                      <div class="flex items-center gap-2">
                        <PhCheckCircle @size={{18}} class="text-emerald-600" />
                        <Text class="text-sm text-emerald-800">Personalized repayment strategy</Text>
                      </div>
                      <div class="flex items-center gap-2">
                        <PhCheckCircle @size={{18}} class="text-emerald-600" />
                        <Text class="text-sm text-emerald-800">PSLF eligibility analysis</Text>
                      </div>
                      <div class="flex items-center gap-2">
                        <PhCheckCircle @size={{18}} class="text-emerald-600" />
                        <Text class="text-sm text-emerald-800">IDR plan comparison</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            {{else if this.isConnecting}}
              {{! CONNECTING STATE: Progress animation }}
              <div class="text-center py-12">
                <div
                  class="w-20 h-20 mx-auto mb-6 bg-indigo-100 rounded-full flex items-center justify-center relative"
                >
                  <PhPlugsConnected @size={{40}} class="text-indigo-600" />
                  <div
                    class="absolute inset-0 rounded-full border-4 border-indigo-200 border-t-indigo-600 animate-spin"
                  ></div>
                </div>
                <Heading @level={{2}} class="text-xl font-bold text-gray-900 mb-2">
                  Securely Connecting...
                </Heading>
                <Text class="text-gray-600 mb-6">
                  We're pulling your loan information from the federal database.
                </Text>

                {{! Progress bar }}
                <div class="max-w-sm mx-auto">
                  <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      class="h-full bg-indigo-600 rounded-full transition-all duration-300"
                      {{style width=(concat this.connectionProgress "%")}}
                    ></div>
                  </div>
                  <div class="flex justify-between mt-2 text-sm text-gray-500">
                    <span>Authenticating</span>
                    <span>{{this.connectionProgress}}%</span>
                  </div>
                </div>

                <div class="mt-8 p-4 bg-blue-50 rounded-lg max-w-md mx-auto">
                  <div class="flex items-start gap-3">
                    <PhShieldCheck @size={{20}} class="text-blue-600 flex-shrink-0 mt-0.5" />
                    <Text class="text-sm text-blue-800 text-left">
                      Your credentials are never stored. We use bank-level encryption and read-only
                      access.
                    </Text>
                  </div>
                </div>
              </div>

            {{else if this.showManualEntry}}
              {{! MANUAL ENTRY STATE }}
              <div class="mb-6">
                <div class="flex items-center justify-between mb-4">
                  <div class="flex items-center gap-3">
                    <button
                      type="button"
                      class="p-1 hover:bg-gray-100 rounded"
                      {{on "click" this.resetConnectionMethod}}
                    >
                      <PhArrowLeft @size={{20}} class="text-gray-500" />
                    </button>
                    <div>
                      <Heading @level={{2}} class="text-xl font-bold text-gray-900">
                        Enter Your Loans Manually
                      </Heading>
                      <Text class="text-sm text-gray-600">
                        Add each loan you want to track
                      </Text>
                    </div>
                  </div>
                </div>

                <div class="space-y-4 mb-6">
                  {{#each this.manualLoans as |loan index|}}
                    <div class="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <div class="flex items-center justify-between mb-3">
                        <Text class="font-medium text-gray-700">Loan #{{index}}</Text>
                        {{#if this.canRemoveManualLoan}}
                          <button
                            type="button"
                            class="p-1 hover:bg-gray-200 rounded text-gray-400 hover:text-red-500"
                            {{on "click" (fn this.removeManualLoan loan.id)}}
                          >
                            <PhX @size={{16}} />
                          </button>
                        {{/if}}
                      </div>
                      <div class="grid grid-cols-2 gap-3">
                        <div>
                          <label class="block text-xs text-gray-500 mb-1">Servicer</label>
                          <select
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="">Select servicer...</option>
                            {{#each this.servicers as |servicer|}}
                              <option value={{servicer.id}}>{{servicer.name}}</option>
                            {{/each}}
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div>
                          <label class="block text-xs text-gray-500 mb-1">Loan Type</label>
                          <select
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="federal">Federal</option>
                            <option value="private">Private</option>
                          </select>
                        </div>
                        <div>
                          <label class="block text-xs text-gray-500 mb-1">Balance</label>
                          <div class="relative">
                            <span
                              class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                            >$</span>
                            <input
                              type="number"
                              placeholder="0"
                              class="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                        </div>
                        <div>
                          <label class="block text-xs text-gray-500 mb-1">Interest Rate</label>
                          <div class="relative">
                            <input
                              type="number"
                              step="0.01"
                              placeholder="5.0"
                              class="w-full pr-7 pl-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <span
                              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                            >%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  {{/each}}
                </div>

                <button
                  type="button"
                  class="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all flex items-center justify-center gap-2"
                  {{on "click" this.addManualLoan}}
                >
                  <PhPlus @size={{18}} />
                  Add Another Loan
                </button>

                <div class="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div class="flex items-start gap-3">
                    <PhInfo @size={{18}} class="text-amber-600 flex-shrink-0 mt-0.5" />
                    <Text class="text-sm text-amber-800">
                      Tip: You can find this information on your loan servicer's website, your
                      latest statement, or by downloading your NSLDS file from StudentAid.gov.
                    </Text>
                  </div>
                </div>
              </div>

            {{else if this.showUploadOption}}
              {{! UPLOAD STATE }}
              <div class="mb-6">
                <div class="flex items-center gap-3 mb-4">
                  <button
                    type="button"
                    class="p-1 hover:bg-gray-100 rounded"
                    {{on "click" this.resetConnectionMethod}}
                  >
                    <PhArrowLeft @size={{20}} class="text-gray-500" />
                  </button>
                  <div>
                    <Heading @level={{2}} class="text-xl font-bold text-gray-900">
                      Upload a Document
                    </Heading>
                    <Text class="text-sm text-gray-600">
                      We'll extract your loan information automatically
                    </Text>
                  </div>
                </div>

                <div
                  class="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-indigo-400 hover:bg-indigo-50/50 transition-all cursor-pointer"
                  role="button"
                  {{on "click" this.handleFileUpload}}
                >
                  <PhCloudArrowUp @size={{48}} class="mx-auto text-gray-400 mb-4" />
                  <Text class="font-medium text-gray-700 mb-2">
                    Drop your file here, or click to browse
                  </Text>
                  <Text class="text-sm text-gray-500">
                    Accepts PDF, PNG, or JPEG up to 10MB
                  </Text>
                </div>

                <div class="mt-6 space-y-3">
                  <Text class="text-sm font-medium text-gray-700">What documents work best:</Text>
                  <div class="grid grid-cols-2 gap-3">
                    <div class="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <PhCheckCircle @size={{16}} class="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <Text class="text-sm text-gray-600">Recent loan statement</Text>
                    </div>
                    <div class="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <PhCheckCircle @size={{16}} class="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <Text class="text-sm text-gray-600">NSLDS report screenshot</Text>
                    </div>
                    <div class="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <PhCheckCircle @size={{16}} class="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <Text class="text-sm text-gray-600">StudentAid.gov download</Text>
                    </div>
                    <div class="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
                      <PhCheckCircle @size={{16}} class="text-emerald-500 flex-shrink-0 mt-0.5" />
                      <Text class="text-sm text-gray-600">Servicer account summary</Text>
                    </div>
                  </div>
                </div>
              </div>

            {{else}}
              {{! INITIAL STATE: Choose connection method }}
              <div class="mb-8">
                <div class="flex items-center gap-3 mb-2">
                  <PhCloudArrowUp @size={{28}} class="text-indigo-600" />
                  <Heading @level={{2}} class="text-2xl font-bold text-gray-900">
                    Upload Your Student Loan Documents
                  </Heading>
                </div>
                <Text class="text-gray-600">
                  Uploading your loan documents unlocks employer contributions, forgiveness
                  tracking, and personalized strategies.
                </Text>
              </div>

              {{! Value Preview }}
              <div
                class="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-5 border border-violet-200 mb-8"
              >
                <div class="flex items-start gap-4">
                  <div class="p-2 bg-violet-100 rounded-lg">
                    <PhSparkle @size={{24}} class="text-violet-600" />
                  </div>
                  <div>
                    <Text class="font-semibold text-violet-900">Connect to unlock up to
                      {{formatCurrency this.unlockableValue.total}}
                      in benefits</Text>
                    <div class="mt-2 flex flex-wrap gap-4">
                      <div class="flex items-center gap-2">
                        <PhHandshake @size={{16}} class="text-violet-600" />
                        <Text class="text-sm text-violet-700">{{formatCurrency
                            this.unlockableValue.section127
                          }}/year employer payments</Text>
                      </div>
                      {{#if this.hasPslfPotential}}
                        <div class="flex items-center gap-2">
                          <PhShieldCheck @size={{16}} class="text-violet-600" />
                          <Text class="text-sm text-violet-700">PSLF forgiveness tracking</Text>
                        </div>
                      {{/if}}
                    </div>
                  </div>
                </div>
              </div>

              {{! Connection Methods }}
              <div class="space-y-4">
                {{! PRIMARY: Instant Connect }}
                <button
                  type="button"
                  class="w-full p-5 border-2 border-indigo-500 bg-indigo-50 rounded-xl text-left transition-all hover:bg-indigo-100 group"
                  {{on "click" this.startInstantConnection}}
                >
                  <div class="flex items-start gap-4">
                    <div
                      class="p-3 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors"
                    >
                      <PhPlugsConnected @size={{28}} class="text-indigo-600" />
                    </div>
                    <div class="flex-1">
                      <div class="flex items-center gap-2">
                        <Text class="font-semibold text-gray-900 text-lg">Connect Instantly</Text>
                        <Chip @intent="success" @size="sm">Recommended</Chip>
                      </div>
                      <Text class="text-gray-600 mt-1">
                        Securely link to StudentAid.gov in seconds. No uploading required.
                      </Text>
                      <div class="flex items-center gap-4 mt-3 text-sm">
                        <div class="flex items-center gap-1 text-emerald-600">
                          <PhShieldCheck @size={{16}} />
                          <span>Bank-level security</span>
                        </div>
                        <div class="flex items-center gap-1 text-emerald-600">
                          <PhLightning @size={{16}} />
                          <span>30 seconds</span>
                        </div>
                      </div>
                    </div>
                    <PhCaretRight
                      @size={{24}}
                      class="text-indigo-400 group-hover:text-indigo-600"
                    />
                  </div>
                </button>

                {{! SECONDARY: Manual Entry }}
                <button
                  type="button"
                  class="w-full p-5 border-2 border-gray-200 rounded-xl text-left transition-all hover:border-gray-300 hover:bg-gray-50 group"
                  {{on "click" this.startManualEntry}}
                >
                  <div class="flex items-start gap-4">
                    <div
                      class="p-3 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors"
                    >
                      <PhPencilSimple @size={{28}} class="text-gray-600" />
                    </div>
                    <div class="flex-1">
                      <Text class="font-semibold text-gray-900 text-lg">Enter Manually</Text>
                      <Text class="text-gray-600 mt-1">
                        Type in your loan details if you have them handy.
                      </Text>
                    </div>
                    <PhCaretRight @size={{24}} class="text-gray-400 group-hover:text-gray-600" />
                  </div>
                </button>

                {{! TERTIARY: Upload }}
                <button
                  type="button"
                  class="w-full p-5 border-2 border-gray-200 rounded-xl text-left transition-all hover:border-gray-300 hover:bg-gray-50 group"
                  {{on "click" this.startUpload}}
                >
                  <div class="flex items-start gap-4">
                    <div
                      class="p-3 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors"
                    >
                      <PhCloudArrowUp @size={{28}} class="text-gray-600" />
                    </div>
                    <div class="flex-1">
                      <Text class="font-semibold text-gray-900 text-lg">Upload a Statement</Text>
                      <Text class="text-gray-600 mt-1">
                        We'll extract loan details from your document.
                      </Text>
                    </div>
                    <PhCaretRight @size={{24}} class="text-gray-400 group-hover:text-gray-600" />
                  </div>
                </button>
              </div>

              {{! Help Link }}
              <div class="mt-6 text-center">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600"
                >
                  <PhQuestion @size={{16}} />
                  <span>Not sure where to find your loan info?</span>
                </button>
              </div>
            {{/if}}

            <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <div class="flex gap-3">
                {{#if (and this.needsLoanLink (not this.isConnecting))}}
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @label="Skip for now"
                    {{on "click" this.skipLoanLink}}
                  />
                {{/if}}
                {{#if this.hasLinkedLoans}}
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="Continue"
                    {{on "click" this.nextStep}}
                  />
                {{else if this.showManualEntry}}
                  <Button
                    @intent="primary"
                    @icon={{PhArrowRight}}
                    @iconPlacement="end"
                    @label="Save Loans"
                    disabled={{not this.hasManualLoans}}
                    {{on "click" this.submitManualLoans}}
                  />
                {{/if}}
              </div>
            </div>
          {{/if}}

          {{! ================================ STEP 3: PSLF CHECK ================================ }}
          {{#if (eq this.currentStepId "pslf")}}
            <div class="mb-8">
              <div class="flex items-center gap-3 mb-2">
                <PhShieldCheck @size={{28}} class="text-blue-600" />
                <Heading @level={{2}} class="text-2xl font-bold text-gray-900">
                  Public Service Loan Forgiveness Check
                </Heading>
              </div>
              <Text class="text-gray-600">
                PSLF can forgive your remaining federal loans after 120 qualifying payments. Let's
                see if you qualify.
              </Text>
            </div>

            {{#if this.hasCompletedPslfCheck}}
              <div class="p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div class="flex items-center gap-4">
                  <div class="p-3 bg-emerald-100 rounded-full">
                    <PhCheckCircle @size={{32}} class="text-emerald-600" />
                  </div>
                  <div>
                    <Text class="font-semibold text-emerald-900 text-lg">PSLF Check Complete!</Text>
                    <Text class="text-emerald-700">
                      We've saved your eligibility status and will track your progress.
                    </Text>
                  </div>
                </div>
              </div>
            {{else}}
              <div class="space-y-6">
                <div>
                  <Text class="font-medium text-gray-700 mb-3">
                    Do you work for a qualifying public service employer?
                  </Text>
                  <Text class="text-sm text-gray-500 mb-4">
                    This includes government (federal, state, local), 501(c)(3) nonprofits, tribal
                    organizations, and some AmeriCorps/Peace Corps positions.
                  </Text>

                  <div class="flex gap-4">
                    <button
                      type="button"
                      class="flex-1 p-5 border-2 rounded-xl transition-all
                        {{if
                          (eq this.worksInPublicService true)
                          'border-emerald-500 bg-emerald-50'
                          'border-gray-200 hover:border-emerald-300'
                        }}"
                      {{on "click" (fn this.setPublicServiceAnswer true)}}
                    >
                      <div class="text-center">
                        <PhBuildings
                          @size={{32}}
                          class={{if
                            (eq this.worksInPublicService true)
                            "text-emerald-600"
                            "text-gray-400"
                          }}
                        />
                        <Text class="font-medium mt-2">Yes</Text>
                      </div>
                    </button>
                    <button
                      type="button"
                      class="flex-1 p-5 border-2 rounded-xl transition-all
                        {{if
                          (eq this.worksInPublicService false)
                          'border-gray-500 bg-gray-50'
                          'border-gray-200 hover:border-gray-300'
                        }}"
                      {{on "click" (fn this.setPublicServiceAnswer false)}}
                    >
                      <div class="text-center">
                        <PhBriefcase
                          @size={{32}}
                          class={{if
                            (eq this.worksInPublicService false)
                            "text-gray-600"
                            "text-gray-400"
                          }}
                        />
                        <Text class="font-medium mt-2">No / Not Sure</Text>
                      </div>
                    </button>
                  </div>
                </div>

                {{#if (eq this.worksInPublicService true)}}
                  <div class="p-5 bg-blue-50 border border-blue-200 rounded-xl">
                    <Text class="font-medium text-blue-900 mb-3">
                      How many years have you worked in public service?
                    </Text>
                    <div class="flex gap-2 flex-wrap">
                      {{#each (array 0 1 2 3 4 5 6 7 8 9 10) as |years|}}
                        <button
                          type="button"
                          class="px-4 py-2 border-2 rounded-lg transition-all
                            {{if
                              (eq this.yearsInService years)
                              'border-blue-500 bg-blue-100 text-blue-700'
                              'border-gray-200 bg-white hover:border-blue-300'
                            }}"
                          {{on "click" (fn this.setYearsInService years)}}
                        >
                          {{#if (eq years 10)}}10+{{else}}{{years}}{{/if}}
                        </button>
                      {{/each}}
                    </div>
                  </div>
                {{/if}}

                {{! PSLF Result }}
                {{#if this.pslfEligibilityResult}}
                  <div
                    class="p-5 rounded-xl border-2
                      {{if
                        this.pslfEligibilityResult.eligible
                        'bg-emerald-50 border-emerald-300'
                        'bg-amber-50 border-amber-300'
                      }}"
                  >
                    <div class="flex items-start gap-4">
                      {{#if this.pslfEligibilityResult.eligible}}
                        <PhSealCheck @size={{32}} class="text-emerald-600 flex-shrink-0" />
                      {{else}}
                        <PhInfo @size={{32}} class="text-amber-600 flex-shrink-0" />
                      {{/if}}
                      <div>
                        <Text
                          class="font-semibold text-lg
                            {{if
                              this.pslfEligibilityResult.eligible
                              'text-emerald-900'
                              'text-amber-900'
                            }}"
                        >
                          {{#if this.pslfEligibilityResult.eligible}}
                            You May Qualify for PSLF!
                          {{else}}
                            PSLF May Not Apply, But You Have Options
                          {{/if}}
                        </Text>
                        <Text
                          class="mt-1
                            {{if
                              this.pslfEligibilityResult.eligible
                              'text-emerald-700'
                              'text-amber-700'
                            }}"
                        >
                          {{this.pslfEligibilityResult.message}}
                        </Text>
                        {{#if this.pslfEligibilityResult.eligible}}
                          <div class="mt-4 grid grid-cols-2 gap-4 p-4 bg-white/50 rounded-lg">
                            <div>
                              <Text class="text-sm text-emerald-700">Payments Remaining</Text>
                              <Text class="text-2xl font-bold text-emerald-900">
                                ~{{this.pslfEligibilityResult.estimatedPayments}}
                              </Text>
                            </div>
                            <div>
                              <Text class="text-sm text-emerald-700">Estimated Forgiveness</Text>
                              <Text class="text-2xl font-bold text-emerald-900">
                                {{formatCurrency this.pslfEligibilityResult.estimatedForgiveness}}
                              </Text>
                            </div>
                          </div>
                        {{/if}}
                      </div>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{/if}}

            <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <Button
                @intent="primary"
                @icon={{PhArrowRight}}
                @iconPlacement="end"
                @label="Continue"
                disabled={{eq this.worksInPublicService null}}
                {{on "click" this.completePslfCheck}}
              />
            </div>
          {{/if}}

          {{! ================================ STEP 4: LRAP DISCOVERY ================================ }}
          {{#if (eq this.currentStepId "lrap")}}
            <div class="mb-8">
              <div class="flex items-center gap-3 mb-2">
                <PhMagnifyingGlass @size={{28}} class="text-violet-600" />
                <Heading @level={{2}} class="text-2xl font-bold text-gray-900">
                  Loan Repayment Assistance Discovery
                </Heading>
              </div>
              <Text class="text-gray-600">
                Based on your employer and profile, you may qualify for additional assistance
                programs beyond your employer benefits.
              </Text>
            </div>

            {{#if this.hasCompletedLrapDiscovery}}
              <div class="p-6 bg-emerald-50 border border-emerald-200 rounded-xl">
                <div class="flex items-center gap-4">
                  <div class="p-3 bg-emerald-100 rounded-full">
                    <PhCheckCircle @size={{32}} class="text-emerald-600" />
                  </div>
                  <div>
                    <Text class="font-semibold text-emerald-900 text-lg">Discovery Complete!</Text>
                    <Text class="text-emerald-700">
                      Visit LRAP Discovery anytime to explore details and apply.
                    </Text>
                  </div>
                </div>
              </div>
            {{else}}
              {{#if this.lrapDiscoveryResult}}
                <div class="p-6 bg-violet-50 border border-violet-200 rounded-xl mb-6">
                  <div class="flex items-start gap-4">
                    <div class="p-3 bg-violet-100 rounded-full flex-shrink-0">
                      <PhSparkle @size={{28}} class="text-violet-600" />
                    </div>
                    <div class="flex-1">
                      <Text class="font-semibold text-lg text-violet-900">
                        We Found
                        {{this.lrapDiscoveryResult.programsFound}}
                        Programs!
                      </Text>
                      <Text class="text-violet-700 mt-1">
                        You may qualify for up to
                        <strong>
                          {{formatCurrency this.lrapDiscoveryResult.potentialValue}}/year
                        </strong>
                        in additional assistance.
                      </Text>

                      <div class="mt-4 space-y-3">
                        {{#each this.lrapDiscoveryResult.programs as |program|}}
                          <div class="flex items-center justify-between p-3 bg-white rounded-lg">
                            <div class="flex items-center gap-3">
                              {{#if (eq program.type "school")}}
                                <PhGraduationCap @size={{20}} class="text-violet-500" />
                              {{else if (eq program.type "state")}}
                                <PhBuildings @size={{20}} class="text-blue-500" />
                              {{else if (eq program.type "federal")}}
                                <PhShieldCheck @size={{20}} class="text-emerald-500" />
                              {{else}}
                                <PhHandshake @size={{20}} class="text-amber-500" />
                              {{/if}}
                              <div>
                                <Text class="font-medium text-gray-900">{{program.name}}</Text>
                                <Text
                                  class="text-sm text-gray-500 capitalize"
                                >{{program.type}}</Text>
                              </div>
                            </div>
                            <Text class="font-bold text-violet-700">
                              {{formatCurrency program.value}}/yr
                            </Text>
                          </div>
                        {{/each}}
                      </div>
                    </div>
                  </div>
                </div>

                <div class="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-6">
                  <div class="flex items-start gap-3">
                    <PhWarning @size={{20}} class="text-amber-600 flex-shrink-0 mt-0.5" />
                    <Text class="text-sm text-amber-800">
                      <strong>Stacking Note:</strong>
                      Some programs have "outside funds" policies that may affect how benefits
                      combine. We'll help you optimize your strategy to maximize total assistance.
                    </Text>
                  </div>
                </div>
              {{/if}}
            {{/if}}

            <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <Button
                @intent="default"
                @appearance="outlined"
                @icon={{PhArrowLeft}}
                @label="Back"
                {{on "click" this.previousStep}}
              />
              <div class="flex gap-3">
                {{#if this.needsLrapDiscovery}}
                  <Button
                    @intent="default"
                    @appearance="outlined"
                    @label="Skip for now"
                    {{on "click" this.skipLrapDiscovery}}
                  />
                {{/if}}
                <Button
                  @intent="primary"
                  @icon={{PhArrowRight}}
                  @iconPlacement="end"
                  @label={{if this.hasCompletedLrapDiscovery "Continue" "Save & Continue"}}
                  {{on "click" this.completeLrapDiscovery}}
                />
              </div>
            </div>
          {{/if}}

          {{! ================================ STEP 5: SET GOAL ================================ }}
          {{#if (eq this.currentStepId "goals")}}
            {{#if this.hasSetGoal}}
              {{! Completion Screen }}
              <div class="text-center">
                <div
                  class="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center"
                >
                  <PhConfetti @size={{48}} class="text-white" @weight="fill" />
                </div>
                <Heading @level={{1}} class="text-3xl font-bold text-gray-900 mb-3">
                  You're All Set!
                </Heading>
                <Text class="text-lg text-gray-600 max-w-md mx-auto mb-8">
                  Your personalized dashboard is ready. Let's start maximizing your benefits!
                </Text>

                {{! Summary }}
                <div class="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-md mx-auto">
                  <Text class="font-medium text-gray-700 mb-4">Your Setup Summary</Text>
                  <div class="space-y-3">
                    <div class="flex items-center gap-3">
                      <PhCheckCircle @size={{20}} class="text-emerald-500" />
                      <Text class="text-gray-600">
                        {{#if this.hasLinkedLoans}}Loans connected{{else}}Loans pending{{/if}}
                      </Text>
                    </div>
                    <div class="flex items-center gap-3">
                      <PhCheckCircle @size={{20}} class="text-emerald-500" />
                      <Text class="text-gray-600">
                        PSLF eligibility:
                        {{#if this.pslfEligibilityResult.eligible}}Likely eligible{{else}}Exploring
                          other options{{/if}}
                      </Text>
                    </div>
                    {{#if this.showLrapStep}}
                      <div class="flex items-center gap-3">
                        <PhCheckCircle @size={{20}} class="text-emerald-500" />
                        <Text class="text-gray-600">
                          LRAP programs found:
                          {{this.lrapDiscoveryResult.programsFound}}
                        </Text>
                      </div>
                    {{/if}}
                    <div class="flex items-center gap-3">
                      <PhCheckCircle @size={{20}} class="text-emerald-500" />
                      <Text class="text-gray-600">
                        Goal:
                        {{this.selectedGoal}}
                      </Text>
                    </div>
                  </div>
                </div>

                <div class="flex justify-center gap-4">
                  <LinkTo @route="authenticated.reimagined.loans">
                    <Button
                      @intent="primary"
                      @size="lg"
                      @icon={{PhArrowRight}}
                      @iconPlacement="end"
                      @label="Go to Dashboard"
                    />
                  </LinkTo>
                </div>
              </div>
            {{else}}
              <div class="mb-8">
                <div class="flex items-center gap-3 mb-2">
                  <PhTarget @size={{28}} class="text-emerald-600" />
                  <Heading @level={{2}} class="text-2xl font-bold text-gray-900">
                    What's Your Primary Goal?
                  </Heading>
                </div>
                <Text class="text-gray-600">
                  This helps us personalize your dashboard and recommendations.
                </Text>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {{#each this.goalOptions as |goal|}}
                  <button
                    type="button"
                    class="p-5 border-2 rounded-xl text-left transition-all
                      {{if
                        (eq this.selectedGoal goal.id)
                        (if
                          (eq goal.color 'amber')
                          'border-amber-500 bg-amber-50'
                          (if
                            (eq goal.color 'blue')
                            'border-blue-500 bg-blue-50'
                            (if
                              (eq goal.color 'violet')
                              'border-violet-500 bg-violet-50'
                              'border-emerald-500 bg-emerald-50'
                            )
                          )
                        )
                        'border-gray-200 hover:border-gray-300'
                      }}"
                    {{on "click" (fn this.selectGoal goal.id)}}
                  >
                    <div class="flex items-start gap-4">
                      <div
                        class="p-3 rounded-lg
                          {{if
                            (eq goal.color 'amber')
                            'bg-amber-100 text-amber-600'
                            (if
                              (eq goal.color 'blue')
                              'bg-blue-100 text-blue-600'
                              (if
                                (eq goal.color 'violet')
                                'bg-violet-100 text-violet-600'
                                'bg-emerald-100 text-emerald-600'
                              )
                            )
                          }}"
                      >
                        <goal.icon @size={{24}} />
                      </div>
                      <div class="flex-1">
                        <Text class="font-semibold text-gray-900">{{goal.title}}</Text>
                        <Text class="text-sm text-gray-600 mt-1">{{goal.description}}</Text>
                      </div>
                      {{#if (eq this.selectedGoal goal.id)}}
                        <PhCheckCircle
                          @size={{24}}
                          class={{if
                            (eq goal.color "amber")
                            "text-amber-600"
                            (if
                              (eq goal.color "blue")
                              "text-blue-600"
                              (if (eq goal.color "violet") "text-violet-600" "text-emerald-600")
                            )
                          }}
                        />
                      {{/if}}
                    </div>
                  </button>
                {{/each}}
              </div>

              <div class="flex justify-between mt-8 pt-6 border-t border-gray-200">
                <Button
                  @intent="default"
                  @appearance="outlined"
                  @icon={{PhArrowLeft}}
                  @label="Back"
                  {{on "click" this.previousStep}}
                />
                <Button
                  @intent="primary"
                  @size="lg"
                  @icon={{PhConfetti}}
                  @iconPlacement="end"
                  @label="Complete Setup"
                  disabled={{not this.selectedGoal}}
                  {{on "click" this.completeOnboarding}}
                />
              </div>
            {{/if}}
          {{/if}}
        </div>
      </VStack>
    </DataIndicator>
  </template>
}
